import { User as FirebaseUser, getAuth } from 'firebase/auth';
import { jwtDecode } from 'jwt-decode';
import * as React from 'react';
import { JWTPayload } from 'src/utils/jwt';

type User =
  | (FirebaseUser &
      Pick<JWTPayload, 'organization' | 'roles'> & {
        appUserId?: JWTPayload['id'];
        loading: boolean;
        user?: FirebaseUser;
      })
  | null;

type ContextState = { user: User };

interface FirebaseAuthProviderProps {
  children: React.ReactNode;
}

const FirebaseAuthContext = React.createContext<ContextState | undefined>(
  undefined,
);

const FirebaseAuthProvider: React.FC<FirebaseAuthProviderProps> = ({
  children,
}: FirebaseAuthProviderProps) => {
  const [user, setUser] = React.useState<User>(null);

  React.useEffect(() => {
    const unsubscribe = getAuth().onIdTokenChanged(async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(Object.assign({}, { loading: false }) as unknown as User);
        return;
      }

      const decodedToken = jwtDecode<JWTPayload>(
        await firebaseUser.getIdToken(),
      );

      setUser(
        Object.assign({}, firebaseUser, {
          organization: decodedToken.organization,
          roles: decodedToken.roles,
          appUserId: decodedToken.id,
          loading: false,
          user: firebaseUser,
        }) as User,
      );
    });

    return unsubscribe;
  }, []);

  return (
    <FirebaseAuthContext.Provider value={{ user }}>
      {children}
    </FirebaseAuthContext.Provider>
  );
};

function useFirebaseAuth() {
  const context = React.useContext(FirebaseAuthContext);
  if (context === undefined) {
    throw new Error(
      'useFirebaseAuth must be used within a FirebaseAuthProvider',
    );
  }

  return context.user;
}

export { FirebaseAuthProvider, useFirebaseAuth };
